/** Limites alinhados ao backend (podem ser refinados via /assistant/status). */
export const ASSISTANT_MAX_IMAGES_DEFAULT = 4;
export const ASSISTANT_MAX_IMAGE_BYTES_DEFAULT = 5 * 1024 * 1024;

const ACCEPT = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

export function isAssistantImageFile(file: File): boolean {
  return ACCEPT.includes(file.type) || /\.(jpe?g|png|gif|webp)$/i.test(file.name);
}

async function loadImage(file: File): Promise<HTMLImageElement> {
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error('INVALID_IMAGE'));
      img.src = url;
    });
    return img;
  } finally {
    URL.revokeObjectURL(url);
  }
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('ENCODE_FAILED'))), type, quality);
  });
}

/**
 * Reduz fotos grandes para acelerar upload e caber nos limites do servidor.
 */
export async function compressAssistantImage(file: File, maxEdge = 2048, maxBytes = ASSISTANT_MAX_IMAGE_BYTES_DEFAULT): Promise<File> {
  if (!isAssistantImageFile(file)) throw new Error('INVALID_IMAGE_TYPE');
  if (file.size <= maxBytes && file.type === 'image/jpeg') return file;
  if (file.type === 'image/gif') {
    if (file.size <= maxBytes) return file;
    throw new Error('GIF_TOO_LARGE');
  }
  const img = await loadImage(file);
  const scale = Math.min(1, maxEdge / Math.max(img.width, img.height, 1));
  const w = Math.max(1, Math.round(img.width * scale));
  const h = Math.max(1, Math.round(img.height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) return file;
  ctx.drawImage(img, 0, 0, w, h);
  let quality = 0.88;
  let blob = await canvasToBlob(canvas, 'image/jpeg', quality);
  while (blob.size > maxBytes && quality > 0.45) {
    quality -= 0.08;
    blob = await canvasToBlob(canvas, 'image/jpeg', quality);
  }
  if (blob.size > maxBytes) throw new Error('IMAGE_TOO_LARGE');
  const base = file.name.replace(/\.[^.]+$/, '') || 'imagem';
  return new File([blob], `${base}.jpg`, { type: 'image/jpeg', lastModified: Date.now() });
}

export interface PendingAssistantImage {
  id: string;
  file: File;
  previewUrl: string;
}

export function createPendingImage(file: File): PendingAssistantImage {
  return {
    id: `img-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
    file,
    previewUrl: URL.createObjectURL(file),
  };
}

export function revokePendingImage(p: PendingAssistantImage): void {
  try {
    URL.revokeObjectURL(p.previewUrl);
  } catch {
    // ignore
  }
}
